/* eslint-disable @typescript-eslint/naming-convention */

/* global navigator */
/* global document */
/* global localStorage */

import { Firehose } from '@aws-sdk/client-firehose';
import { type CognitoIdentityCredentialProvider, fromCognitoIdentityPool } from '@aws-sdk/credential-providers';

import { type ClickstreamData } from './clickstream-interfaces';

class Clickstream {
    private readonly credentials: CognitoIdentityCredentialProvider;
    private readonly firehose: Firehose;
    private readonly deliveryStreamName: string;

    constructor(identityPoolId: string, bindArg: string, deliveryStreamName = "WebsiteEvents") {
        this.deliveryStreamName = deliveryStreamName;

        this.credentials = fromCognitoIdentityPool({
            clientConfig: { region: "ap-southeast-2" },
            identityPoolId: identityPoolId,
        });

        this.firehose = new Firehose({
            region: "ap-southeast-2",
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            credentials: this.credentials
        });

        document.addEventListener("click", async (e: MouseEvent) => {
            if ((e.target as HTMLElement)?.closest(bindArg))
            {
                const elem = e.target;
                const enc = new TextEncoder();

                try {
                    await this.firehose.putRecord({
                        Record: {
                            Data: enc.encode(JSON.stringify(await this.getUserData("Click", elem as HTMLElement, null)))
                        },
                        DeliveryStreamName: deliveryStreamName
                    });
                } catch (error) {
                    console.error(error);
                }
            }
        }, true);

        void this.visit();
    }

    getUserData = async (eventType: string, element: HTMLElement | null, additionaldata: unknown) => {
        let userIdentityId = localStorage.getItem("website-user-identity");
        const credentials = await this.credentials();

        if (!userIdentityId) {
            localStorage.setItem("website-user-identity", credentials.identityId)
            userIdentityId = localStorage.getItem("website-user-identity");
        }

        const data: ClickstreamData = {
            IdentityId: userIdentityId!,
            AWSIdentityId: credentials.identityId,
            URL: location.href,
            Event: eventType,
            SitecoreId: "",
            Location: "",
            Browser: this.getBrowser(),
            DateTime: new Date().toISOString()
        };

        const  salesforceId = this.getUrlParameter("sid");

        if (salesforceId !== null) {
            data.SalesforceId = salesforceId;
        }

        if (navigator.userAgent) {
            data.UserAgent = navigator.userAgent;
        }

        if (additionaldata !== null) {
            data.AdditionalData = additionaldata;
        }

        data.Session = credentials.sessionToken;

        if (element) {
            data.Element = element.outerHTML;

            const href = element.getAttribute("href");

            if (href) {
                data.Link = href;
            }
        }

        if (document.referrer) {
            data.Referrer = document.referrer;
        }

        const OSName = this.getOSName();
        if (OSName !== "") {
            data.OSPlatform = OSName;
        }

        return data;
    }

    getUrlParameter(name: string) {
        name = name.replace(/\[/, "\\[").replace(/]/, "\\]");
        const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
        const results = regex.exec(location.search);
        return results === null ? "" : decodeURIComponent(results[1].replaceAll('+', " "));
    }

    getBrowser() {
        const nAgt = navigator.userAgent;
        let browserName = navigator.appName;
        let fullVersion = "" + Number.parseFloat(navigator.appVersion);
        let nameOffset, verOffset, ix;

        // In Opera, the true version is after "Opera" or after "Version"
        if ((verOffset = nAgt.indexOf("Opera")) != -1) {
            browserName = "Opera";
            fullVersion = nAgt.slice(Math.max(0, verOffset + 6));
            if ((verOffset = nAgt.indexOf("Version")) != -1)
                fullVersion = nAgt.slice(Math.max(0, verOffset + 8));
        }
        // In MSIE, the true version is after "MSIE" in userAgent
        else if ((verOffset = nAgt.indexOf("MSIE")) != -1) {
            browserName = "Microsoft Internet Explorer";
            fullVersion = nAgt.slice(Math.max(0, verOffset + 5));
        }
        // In Edge, the true version is after "Edg"
        else if ((verOffset = nAgt.indexOf("Edg")) != -1) {
            browserName = "Microsoft Edge";
            fullVersion = nAgt.slice(Math.max(0, verOffset + 4));
        }
        // In Chrome, the true version is after "Chrome"
        else if ((verOffset = nAgt.indexOf("Chrome")) != -1) {
            browserName = "Chrome";
            fullVersion = nAgt.slice(Math.max(0, verOffset + 7));
        }
        // In Safari, the true version is after "Safari" or after "Version"
        else if ((verOffset = nAgt.indexOf("Safari")) != -1) {
            browserName = "Safari";
            fullVersion = nAgt.slice(Math.max(0, verOffset + 7));
            if ((verOffset = nAgt.indexOf("Version")) != -1)
                fullVersion = nAgt.slice(Math.max(0, verOffset + 8));
        }
        // In Firefox, the true version is after "Firefox"
        else if ((verOffset = nAgt.indexOf("Firefox")) != -1) {
            browserName = "Firefox";
            fullVersion = nAgt.slice(Math.max(0, verOffset + 8));
        }
        // In most other browsers, "name/version" is at the end of userAgent
        else if ((nameOffset = nAgt.lastIndexOf(" ") + 1) <
            (verOffset = nAgt.lastIndexOf("/"))) {
            browserName = nAgt.slice(nameOffset, verOffset);
            fullVersion = nAgt.slice(Math.max(0, verOffset + 1));
            if (browserName.toLowerCase() == browserName.toUpperCase()) {
                browserName = navigator.appName;
            }
        }

        // trim the fullVersion string at semicolon/space if present
        if ((ix = fullVersion.indexOf(";")) != -1)
            fullVersion = fullVersion.slice(0, Math.max(0, ix));
        if ((ix = fullVersion.indexOf(" ")) != -1)
            fullVersion = fullVersion.slice(0, Math.max(0, ix));

        const majorVersion = Number.parseInt("" + fullVersion, 10);
        if (Number.isNaN(majorVersion)) {
            fullVersion = "" + Number.parseFloat(navigator.appVersion);
        }

        return browserName + " " + fullVersion;
    };

    getOSName() {
        const OSName = navigator.platform;

        if (navigator.platform.includes("Win")) return "Windows";
        if (navigator.platform.includes("Mac")) return "MacOS";
        if (navigator.platform.includes("X11")) return "UNIX";
        if (navigator.platform.includes("Linux armv7l")) return "Android";
        if (navigator.platform.includes("Linux")) return "Linux";
        if (navigator.platform.includes("Android")) return "Android";

        return OSName;
    };

    async visit() {
        const enc = new TextEncoder();
        try {
            await this.firehose.putRecord({
                Record: {
                    Data: enc.encode(JSON.stringify(await this.getUserData("Page Visit", null, null)))
                },
                DeliveryStreamName: this.deliveryStreamName
            });
        } catch (error) {
            console.error(error);
        }
    }
}

export { Clickstream }
